import React, { useEffect, useMemo, useState } from "react";
import { Alert, Avatar, Badge, Button, Card, Field, IconBtn, Input, Spinner, Tabs } from "../../design-system";
import { Icon, SupSection, SupSelect, CategoryIcon } from "./atoms";
import {
  getSupportTechniciansConfig, setSupportTechnicianConfig,
  getSupportCategories, createSupportCategory, updateSupportCategory, deleteSupportCategory,
} from "../../helpers/kronaly_helper";

const unwrap = (r) => r?.data ?? r;
const COLORS = [
  { value: "info", label: "Azul" }, { value: "brand", label: "Verde" }, { value: "warning", label: "Ámbar" },
  { value: "pending", label: "Violeta" }, { value: "neutral", label: "Gris" }, { value: "success", label: "Éxito" }, { value: "danger", label: "Rojo" },
];
const SWATCH = { info: "var(--kr-info-500)", brand: "var(--kr-action)", warning: "var(--kr-warning-500)", pending: "var(--kr-pending-500)", neutral: "var(--kr-neutral-400)", success: "var(--kr-success-500)", danger: "var(--kr-danger-500)" };
// Iconos disponibles (nombres que el wrapper Icon mapea a Lucide).
const ICON_CHOICES = ["cloud", "mail", "message", "monitor", "laptop", "smartphone", "printer", "server", "package", "folder", "key", "tag", "lock", "wrench", "building", "headphones", "keyboard", "mouse", "zap", "bookOpen", "fileText", "ticket", "users", "calendar", "phone", "help"];

function ColorSwatches({ value, onChange }) {
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", height: 36 }}>
      {COLORS.map((c) => (
        <button key={c.value} type="button" title={c.label} onClick={() => onChange(c.value)} style={{
          width: 24, height: 24, borderRadius: 999, background: SWATCH[c.value], cursor: "pointer",
          border: value === c.value ? "2px solid var(--kr-fg-strong)" : "2px solid var(--kr-border)",
          outline: value === c.value ? "2px solid #fff" : "none", outlineOffset: -4,
        }} />
      ))}
    </div>
  );
}

function IconPicker({ value, color, onChange }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(46px, 1fr))", gap: 8 }}>
      {ICON_CHOICES.map((name) => {
        const active = value === name;
        return (
          <button key={name} type="button" onClick={() => onChange(name)} title={name} style={{
            display: "flex", alignItems: "center", justifyContent: "center", height: 46, borderRadius: 8, cursor: "pointer",
            border: `1px solid ${active ? "var(--kr-action)" : "var(--kr-border)"}`,
            background: active ? "var(--kr-action-soft-bg)" : "#fff",
          }}>
            <CategoryIcon icon={name} color={color} size={30} iconSize={16} />
          </button>
        );
      })}
    </div>
  );
}
const ROLE_LABEL = { owner: "Propietario", admin: "Administrador", hr: "RRHH", manager: "Manager", employee: "Empleado" };
const cargo = (m) => m.position || ROLE_LABEL[m.role] || m.role;

/**
 * Panel de configuración de Soporte (Técnicos + Categorías). Se monta como
 * una sección dentro de la Configuración del workspace.
 */
export function SoporteConfigPanel() {
  const [tab, setTab] = useState("tecnicos");
  return (
    <Card padded={false}>
      <div style={{ padding: "8px 20px 0" }}>
        <Tabs value={tab} onChange={setTab} tabs={[{ id: "tecnicos", label: "Técnicos" }, { id: "categorias", label: "Categorías" }]} />
      </div>
      <div style={{ padding: 20 }}>
        {tab === "tecnicos" ? <TecnicosSection /> : <CategoriasSection />}
      </div>
    </Card>
  );
}

// ── Técnicos ────────────────────────────────────────────────────────
function TecnicosSection() {
  const [rows, setRows] = useState([]);
  const [roots, setRoots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [assignFor, setAssignFor] = useState(null);
  const [removing, setRemoving] = useState(null);

  const load = () => {
    setLoading(true);
    return Promise.all([getSupportTechniciansConfig(), getSupportCategories()])
      .then(([t, c]) => {
        setRows(unwrap(t) || []);
        setRoots((unwrap(c) || []).filter((x) => !x.parent_id));
        setError(null);
      })
      .catch((e) => setError(e?.message || String(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const technicians = useMemo(() => rows.filter((r) => r.is_technician), [rows]);
  const candidates = useMemo(() => rows.filter((r) => !r.is_technician), [rows]);
  const catById = useMemo(() => Object.fromEntries(roots.map((c) => [c.id, c])), [roots]);

  const save = async (membershipId, isTech, categoryIds, reassignTo = null) => {
    setBusy(true); setError(null);
    try {
      await setSupportTechnicianConfig(membershipId, { is_technician: isTech, category_ids: categoryIds, reassign_to: reassignTo });
      await load();
      return true;
    } catch (e) { setError(e?.message || String(e)); return false; }
    finally { setBusy(false); }
  };

  const handleRemove = (m) => {
    if ((m.open_tickets || 0) > 0) setRemoving(m);
    else save(m.membership_id, false, []);
  };

  if (loading) return <div style={{ display: "flex", justifyContent: "center", padding: "32px 0" }}><Spinner size={20} /></div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {error && <Alert tone="danger" title="Error">{error}</Alert>}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12 }}>
        <p style={{ fontSize: 13, color: "var(--kr-fg-muted)", margin: 0, maxWidth: 620 }}>
          Los técnicos ven y resuelven todos los tickets. Asígnales categorías para que se les repartan automáticamente los tickets nuevos de esas categorías.
        </p>
        <Button variant="primary" icon="Plus" onClick={() => setShowAdd(true)}>Agregar técnico</Button>
      </div>

      {technicians.length === 0 ? (
        <div style={{ padding: "40px 24px", textAlign: "center", color: "var(--kr-fg-muted)", border: "1px dashed var(--kr-border)", borderRadius: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--kr-fg-strong)", marginBottom: 4 }}>Aún no hay técnicos</div>
          <div style={{ fontSize: 13 }}>Agrega miembros del equipo para que puedan resolver tickets.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {technicians.map((m) => (
            <div key={m.membership_id} style={{ border: "1px solid var(--kr-border-subtle)", borderRadius: 10, padding: 14, opacity: busy ? 0.7 : 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Avatar name={m.name || "?"} size={38} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "var(--kr-fg-strong)" }}>{m.name || "—"}</div>
                  <div style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>{cargo(m)}</div>
                </div>
                {(m.open_tickets || 0) > 0 && (
                  <Badge tone="info" variant="soft">{m.open_tickets} {m.open_tickets === 1 ? "ticket" : "tickets"} abiertos</Badge>
                )}
                <IconBtn icon="Trash2" title="Quitar como técnico" onClick={() => handleRemove(m)} />
              </div>
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--kr-border-subtle)", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600 }}>Categorías por defecto</span>
                {(m.category_ids || []).length === 0 && <span style={{ fontSize: 12, color: "var(--kr-fg-subtle)" }}>Ninguna</span>}
                {(m.category_ids || []).map((id) => catById[id] && (
                  <span key={id} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--kr-fg-strong)", fontWeight: 500 }}>
                    <Icon name={catById[id].icon || "help"} size={13} stroke="var(--kr-fg-muted)" />{catById[id].name}
                  </span>
                ))}
                <div style={{ flex: 1 }} />
                <Button variant="secondary" size="sm" icon="Plus" onClick={() => setAssignFor(m)}>Asignar</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <AddTechnicianDrawer
          candidates={candidates}
          busy={busy}
          onClose={() => setShowAdd(false)}
          onAdd={async (membershipId) => { const ok = await save(membershipId, true, []); if (ok) setShowAdd(false); }}
        />
      )}
      {assignFor && (
        <AssignCategoriesDrawer
          roots={roots}
          tech={assignFor}
          busy={busy}
          onClose={() => setAssignFor(null)}
          onSave={async (ids) => { const ok = await save(assignFor.membership_id, true, ids); if (ok) setAssignFor(null); }}
        />
      )}
      {removing && (
        <RemoveTechnicianDrawer
          tech={removing}
          others={technicians.filter((t) => t.membership_id !== removing.membership_id)}
          busy={busy}
          onClose={() => setRemoving(null)}
          onConfirm={async (reassignTo) => { const ok = await save(removing.membership_id, false, [], reassignTo); if (ok) setRemoving(null); }}
        />
      )}
    </div>
  );
}

function RemoveTechnicianDrawer({ tech, others, onConfirm, onClose, busy }) {
  const [mode, setMode] = useState("unassign"); // unassign | reassign
  const [target, setTarget] = useState("");
  const canConfirm = !busy && (mode === "unassign" || (mode === "reassign" && target));
  return (
    <Drawer title="Quitar técnico" subtitle={tech.name} onClose={onClose}
      footer={<>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button variant="danger" icon="Trash2" disabled={!canConfirm} onClick={() => onConfirm(mode === "reassign" ? target : null)}>Quitar técnico</Button>
      </>}>
      <Alert tone="warning" title={`Tiene ${tech.open_tickets} ${tech.open_tickets === 1 ? "ticket abierto" : "tickets abiertos"}`}>
        Dejará de ser técnico y no podrá gestionar tickets. ¿Qué hacemos con sus tickets abiertos?
      </Alert>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 14 }}>
        <button onClick={() => setMode("unassign")} style={optStyle(mode === "unassign")}>
          <span style={{ fontWeight: 600, color: "var(--kr-fg-strong)", fontSize: 13 }}>Dejar sin asignar</span>
          <span style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>Vuelven a la cola para que otro técnico los tome.</span>
        </button>
        <button onClick={() => setMode("reassign")} style={optStyle(mode === "reassign")}>
          <span style={{ fontWeight: 600, color: "var(--kr-fg-strong)", fontSize: 13 }}>Reasignar a otro técnico</span>
          <span style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>Se transfieren todos sus tickets abiertos.</span>
        </button>
        {mode === "reassign" && (
          <SupSelect value={target} onChange={setTarget} placeholder="Selecciona un técnico…"
            options={others.map((o) => ({ value: o.membership_id, label: o.name }))} />
        )}
        {mode === "reassign" && others.length === 0 && (
          <span style={{ fontSize: 12, color: "var(--kr-danger-700)" }}>No hay otros técnicos disponibles. Usa "dejar sin asignar".</span>
        )}
      </div>
    </Drawer>
  );
}

function optStyle(active) {
  return {
    display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start", textAlign: "left",
    padding: "12px 14px", borderRadius: 10, cursor: "pointer",
    border: `1px solid ${active ? "var(--kr-action)" : "var(--kr-border)"}`,
    background: active ? "var(--kr-action-soft-bg)" : "#fff",
  };
}

function Drawer({ title, subtitle, children, footer, onClose, width = 520 }) {
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,19,22,0.45)", zIndex: 92 }} />
      <aside style={{ position: "fixed", top: 0, right: 0, bottom: 0, width, maxWidth: "100vw", background: "#fff", borderLeft: "1px solid var(--kr-border)", boxShadow: "var(--kr-shadow-lg)", zIndex: 93, display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "18px 24px", borderBottom: "1px solid var(--kr-border-subtle)", display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, fontFamily: "var(--kr-font-display)", fontSize: 20, fontWeight: 700, color: "var(--kr-fg-strong)" }}>{title}</h2>
            {subtitle && <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginTop: 2 }}>{subtitle}</div>}
          </div>
          <IconBtn icon="X" onClick={onClose} title="Cerrar" />
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: 20, background: "var(--kr-neutral-50)" }}>{children}</div>
        {footer && <div style={{ padding: "14px 24px", borderTop: "1px solid var(--kr-border-subtle)", display: "flex", justifyContent: "flex-end", gap: 10, background: "#fff" }}>{footer}</div>}
      </aside>
    </>
  );
}

function AddTechnicianDrawer({ candidates, onAdd, onClose, busy }) {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const list = candidates.filter((m) => !q.trim() || (m.name || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <Drawer title="Agregar técnico" subtitle="Selecciona un miembro del equipo" onClose={onClose}
      footer={<>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button variant="primary" icon="Check" disabled={!sel || busy} onClick={() => onAdd(sel)}>Agregar</Button>
      </>}>
      <div style={{ marginBottom: 12 }}>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre…" icon="Search" autoFocus />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {list.map((m) => {
          const active = sel === m.membership_id;
          return (
            <button key={m.membership_id} onClick={() => setSel(active ? null : m.membership_id)} style={{
              display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", textAlign: "left",
              border: `1px solid ${active ? "var(--kr-action)" : "var(--kr-border)"}`,
              background: active ? "var(--kr-action-soft-bg)" : "#fff", borderRadius: 10, cursor: "pointer",
            }}>
              <Avatar name={m.name || "?"} size={34} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--kr-fg-strong)" }}>{m.name || "—"}</div>
                <div style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>{cargo(m)}</div>
              </div>
              {active && <Icon name="check" size={16} stroke="var(--kr-action)" />}
            </button>
          );
        })}
        {list.length === 0 && <div style={{ fontSize: 13, color: "var(--kr-fg-subtle)", padding: "16px 0", textAlign: "center" }}>No hay miembros que coincidan.</div>}
      </div>
    </Drawer>
  );
}

function AssignCategoriesDrawer({ roots, tech, onSave, onClose, busy }) {
  const [sel, setSel] = useState(() => new Set(tech.category_ids || []));
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  return (
    <Drawer title="Categorías por defecto" subtitle={tech.name} onClose={onClose}
      footer={<>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button variant="primary" icon="Check" disabled={busy} onClick={() => onSave(Array.from(sel))}>Guardar</Button>
      </>}>
      <p style={{ fontSize: 12, color: "var(--kr-fg-muted)", margin: "0 0 12px" }}>Los tickets nuevos de estas categorías se le repartirán automáticamente.</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {roots.map((c) => {
          const active = sel.has(c.id);
          return (
            <button key={c.id} onClick={() => toggle(c.id)} style={{
              display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", textAlign: "left",
              border: `1px solid ${active ? "var(--kr-action)" : "var(--kr-border)"}`,
              background: active ? "var(--kr-action-soft-bg)" : "#fff", borderRadius: 10, cursor: "pointer",
            }}>
              <CategoryIcon icon={c.icon} color={c.color} size={32} />
              <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "var(--kr-fg-strong)" }}>{c.name}</span>
              <span style={{ width: 18, height: 18, borderRadius: 5, border: `1.5px solid ${active ? "var(--kr-action)" : "var(--kr-border-strong)"}`, background: active ? "var(--kr-action)" : "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                {active && <Icon name="check" size={12} stroke="#fff" />}
              </span>
            </button>
          );
        })}
        {roots.length === 0 && <div style={{ fontSize: 13, color: "var(--kr-fg-subtle)" }}>No hay categorías; créalas en la pestaña "Categorías".</div>}
      </div>
    </Drawer>
  );
}

// ── Categorías ──────────────────────────────────────────────────────
function CategoriasSection() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", icon: "", color: "info" });
  const [subParent, setSubParent] = useState(null);
  const [subName, setSubName] = useState("");

  const load = () => {
    setLoading(true);
    return getSupportCategories()
      .then((r) => { setCats(unwrap(r) || []); setError(null); })
      .catch((e) => setError(e?.message || String(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const roots = useMemo(() => cats.filter((c) => !c.parent_id), [cats]);
  const subsOf = (id) => cats.filter((c) => c.parent_id === id);

  const run = async (fn) => { setError(null); try { await fn(); await load(); } catch (e) { setError(e?.message || String(e)); } };

  const addRoot = () => {
    if (!form.name.trim()) return;
    run(async () => {
      await createSupportCategory({ name: form.name.trim(), icon: form.icon.trim() || null, color: form.color });
      setForm({ name: "", icon: "", color: "info" }); setCreating(false);
    });
  };
  const addSub = (parentId) => {
    if (!subName.trim()) return;
    run(async () => { await createSupportCategory({ parent_id: parentId, name: subName.trim() }); setSubName(""); setSubParent(null); });
  };
  const toggleActive = (c) => run(() => updateSupportCategory(c.id, { name: c.name, icon: c.icon, color: c.color, position: c.position, is_active: !c.is_active }));
  const remove = (c) => run(() => deleteSupportCategory(c.id));

  if (loading) return <div style={{ display: "flex", justifyContent: "center", padding: "32px 0" }}><Spinner size={20} /></div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {error && <Alert tone="danger" title="Error">{error}</Alert>}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="primary" size="sm" icon="Plus" onClick={() => setCreating((c) => !c)}>Nueva categoría</Button>
      </div>
      {creating && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: 16, border: "1px solid var(--kr-border-subtle)", borderRadius: 10, background: "var(--kr-neutral-25)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <CategoryIcon icon={form.icon || "help"} color={form.color} size={44} iconSize={22} />
            <div style={{ flex: 1 }}>
              <Field label="Nombre"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Microsoft 365" autoFocus /></Field>
            </div>
          </div>
          <Field label="Color"><ColorSwatches value={form.color} onChange={(v) => setForm({ ...form, color: v })} /></Field>
          <Field label="Icono"><IconPicker value={form.icon} color={form.color} onChange={(v) => setForm({ ...form, icon: v })} /></Field>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button variant="secondary" size="sm" onClick={() => setCreating(false)}>Cancelar</Button>
            <Button variant="primary" size="sm" icon="Check" disabled={!form.name.trim()} onClick={addRoot}>Crear categoría</Button>
          </div>
        </div>
      )}
      {roots.map((c) => (
        <div key={c.id} style={{ border: "1px solid var(--kr-border-subtle)", borderRadius: 10, padding: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <CategoryIcon icon={c.icon} color={c.color} size={32} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--kr-fg-strong)" }}>{c.name}{!c.is_active && <Badge tone="neutral" variant="soft" style={{ marginLeft: 8 }}>Inactiva</Badge>}</div>
              <div style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>{subsOf(c.id).length} subcategorías</div>
            </div>
            <Button variant="ghost" size="sm" icon="Plus" onClick={() => { setSubParent(subParent === c.id ? null : c.id); setSubName(""); }}>Subcategoría</Button>
            <IconBtn icon={c.is_active ? "EyeOff" : "Eye"} title={c.is_active ? "Desactivar" : "Activar"} onClick={() => toggleActive(c)} />
            <IconBtn icon="Trash2" title="Eliminar" onClick={() => remove(c)} />
          </div>
          {subParent === c.id && (
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <Input value={subName} onChange={(e) => setSubName(e.target.value)} placeholder="Nueva subcategoría…" style={{ flex: 1, maxWidth: 320 }} />
              <Button variant="secondary" size="sm" icon="Check" disabled={!subName.trim()} onClick={() => addSub(c.id)}>Añadir</Button>
            </div>
          )}
          {subsOf(c.id).length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 12 }}>
              {subsOf(c.id).map((s) => (
                <span key={s.id} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "5px 6px 5px 10px", border: "1px solid var(--kr-border)", borderRadius: 999, background: "#fff", fontSize: 12, color: "var(--kr-fg-strong)", fontWeight: 500 }}>
                  {s.name}
                  <button onClick={() => remove(s)} title="Eliminar" style={{ border: "none", background: "transparent", color: "var(--kr-fg-muted)", cursor: "pointer", display: "inline-flex", padding: 0 }}><Icon name="x" size={13} /></button>
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
      {roots.length === 0 && <p style={{ fontSize: 13, color: "var(--kr-fg-subtle)", margin: 0 }}>Aún no hay categorías. Crea la primera.</p>}
    </div>
  );
}

export default SoporteConfigPanel;
